import { quoteQuery } from "@common/api/quoting/quote"
import { currentUserQuery } from "@common/api/users"
import { Button } from "@common/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@common/components/ui/dialog"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import useCustomToast from "@/hooks/use-custom-toast"
import { addTaskFromTemplate } from "@/routes/_layout/quoting_/quotes/-lib/ops"
import {
  TemplateList,
  TemplatePreview,
  useSelectedTemplate,
} from "../-components/template-picker"
import { useEditable } from "../-lib/editable"
import { useQuoteUpdate } from "../-lib/update"

const searchSchema = z.object({
  template: z.string().optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/quotes_/$quoteId/tasks/from-template",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, params }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (
      !hasPermission(me, "quote:update") ||
      !hasPermission(me, "canned_work_package:read")
    ) {
      throw redirect({ to: "/quotes/$quoteId", params })
    }
  },
  component: NewTaskFromTemplateDialog,
})

function NewTaskFromTemplateDialog(): React.ReactElement {
  const { quoteId } = Route.useParams()
  const { template } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const editable = useEditable(quote)
  const { update, pending } = useQuoteUpdate(quoteId)
  const chosen = useSelectedTemplate(template)

  const close = () =>
    navigate({
      to: "/quotes/$quoteId",
      params: { quoteId },
      search: ({ template: _template, ...rest }) => rest,
    })
  const select = (id: string) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, template: id }),
      replace: true,
    })

  const confirm = () =>
    chosen &&
    update(addTaskFromTemplate(chosen.id), ({ created_task_ids }) => {
      const [taskId] = created_task_ids
      showToast(
        "Task added",
        `${chosen.name} added from its template.`,
        "success",
      )
      if (!taskId) return close()
      navigate({
        to: "/quotes/$quoteId/tasks/$taskId",
        params: { quoteId, taskId },
        search: ({ template: _template, ...rest }) => rest,
      })
    })

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>New task from template</DialogTitle>
          <DialogDescription>
            Adds a task with the template's description, charges, steps and
            resolution.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <TemplateList
            suggestions={[]}
            selected={template}
            onSelect={select}
          />
          <TemplatePreview template={chosen} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!chosen || pending || !editable} onClick={confirm}>
            {pending ? "Adding…" : "Add task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
